import type { CatalogMatchDetail, PlayerCompareState } from "@livcg/schema";

export type PickerRow = {
  id: string;
  number: number | null;
  firstName: string;
  lastName: string;
  position: string | null;
  /** present when it comes from the roster, null in the players fallback. */
  isStarter: boolean | null;
  photoUrl: string | null;
};

/** Picker rows: match_players (roster) if present — the number from the match squad,
 *  the photo from the players table; if the roster is empty, all of the team's players. */
export function pickerRows(detail: CatalogMatchDetail | null, side: "home" | "away"): PickerRow[] {
  if (!detail) return [];
  const block = detail[side];
  const photos = new Map(block.players.map((p) => [p.id, p.photoUrl ?? null]));
  const rows: PickerRow[] =
    block.roster.length > 0
      ? block.roster.map((r) => ({
          id: r.playerId,
          number: r.number,
          firstName: r.firstName,
          lastName: r.lastName,
          position: r.position,
          isStarter: r.isStarter,
          photoUrl: photos.get(r.playerId) ?? null,
        }))
      : block.players.map((p) => ({
          id: p.id,
          number: p.number,
          firstName: p.firstName,
          lastName: p.lastName,
          position: p.position ?? null,
          isStarter: null,
          photoUrl: p.photoUrl ?? null,
        }));
  return rows.sort((a, b) => (a.number ?? 999) - (b.number ?? 999));
}

/** The opposite side's pick fixes the position: only same-position players are
 *  compared. The side's own current pick stays listed (even if it differs)
 *  so it can still be seen and cleared. No position → no restriction. */
export function restrictToPosition(
  rows: PickerRow[],
  position: string | null,
  keepId: string | null,
): PickerRow[] {
  if (!position) return rows;
  return rows.filter((r) => r.position === position || r.id === keepId);
}

export type JerseyMismatch = {
  id: string;
  teamId: string;
  name: string;
  pfl: number | null;
  hudl: number;
};

/** Linked players whose PFL shirt number differs from the Hudl (dostats)
 *  lineup. PFL stays the source — this is only a warning for the operator. */
export function jerseyMismatches(
  players: {
    id: string;
    teamId: string;
    firstName: string;
    lastName: string;
    number: number | null;
    dostatsId: number | null;
  }[],
  lineup: { id: number; jersey: number | null }[],
): JerseyMismatch[] {
  const jerseyOf = new Map(lineup.map((l) => [l.id, l.jersey]));
  const out: JerseyMismatch[] = [];
  for (const p of players) {
    if (p.dostatsId === null) continue;
    const hudl = jerseyOf.get(p.dostatsId);
    if (hudl == null || hudl === p.number) continue;
    const name = `${p.firstName} ${p.lastName}`.trim();
    out.push({ id: p.id, teamId: p.teamId, name, pfl: p.number, hudl });
  }
  return out.sort((a, b) => a.teamId.localeCompare(b.teamId) || (a.pfl ?? 999) - (b.pfl ?? 999));
}

export function filterRows(rows: PickerRow[], query: string): PickerRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => {
    if (r.number !== null && String(r.number).startsWith(q)) return true;
    const full = `${r.firstName} ${r.lastName}`.toLowerCase();
    return (
      r.firstName.toLowerCase().includes(q) ||
      r.lastName.toLowerCase().includes(q) ||
      full.includes(q)
    );
  });
}

const SIDE_UZ: Record<string, string> = { home: "uy", away: "mehmon" };

/** Extension error code → operator text
 *  (if the code is unknown it is shown as is). */
export function describeCompareError(code: string | null): string {
  if (!code) return "";
  const [head, ...rest] = code.split(":");
  const tail = rest.join(":");
  switch (head) {
    case "no_selection":
      return "Ikkala o'yinchini tanlang";
    case "context_missing":
      return "O'yin ma'lumoti topilmadi (DB)";
    case "match_unmapped":
      return "dostats o'yini xaritalanmagan — Xaritalash bo'limida bog'lang";
    case "match_not_in_source":
      return "dostats'da bu o'yin topilmadi";
    case "player_not_found":
      return tail ? `O'yinchi topilmadi (${SIDE_UZ[tail] ?? tail})` : "O'yinchi topilmadi";
    case "player_unmapped":
      return tail
        ? "O'yinchi dostats bilan bog'lanmagan (" +
            `${SIDE_UZ[tail] ?? tail}) — Xaritalash bo'limida bog'lang`
        : "O'yinchi dostats bilan bog'lanmagan — Xaritalash bo'limida bog'lang";
    case "position_missing":
      return tail
        ? "Uy o'yinchisining pozitsiyasi yo'q — ko'rsatkich to'plami aniqlanmadi"
        : "O'yinchining pozitsiyasi yo'q — ko'rsatkich to'plami aniqlanmadi";
    case "no_match_stats":
      return "Bu o'yinda o'yinchi uchun ma'lumot hali yo'q (maydonga tushmagan bo'lishi mumkin)";
    case "no_stats":
      return "Bu o'yinchida ko'rsatadigan ko'rsatkich yo'q (hammasi nol yoki ma'lumot yo'q)";
    case "dostats_disabled":
      return "dostats sozlanmagan (.env DOSTATS_CLIENT_ID/SECRET)";
    case "snapshot_failed":
      return "Snapshot olib bo'lmadi";
    case "project_not_found":
      return "Loyiha topilmadi";
    case "source": {
      const [kind, ...msg] = rest;
      if (kind === "timeout") {
        return "dostats manbasi javob bermadi (timeout) — qayta urinib ko'ring";
      }
      return `dostats manba xatosi (${kind}): ${msg.join(":")}`;
    }
    case "internal":
      return `Ichki xato: ${tail}`;
    default:
      return `Xato: ${code}`;
  }
}

export function describeCompareWarning(warning: string | null): string | null {
  switch (warning) {
    case "position_mismatch":
      return "Pozitsiyalar farq qiladi — uy o'yinchisining ko'rsatkich " + "to'plami ishlatiladi";
    case "no_snapshot":
      return "1-taym snapshot yo'q — joriy jonli qiymatlar ko'rsatilmoqda";
    default:
      return null;
  }
}

export function statusTone(
  status: PlayerCompareState["status"],
): "neutral" | "warning" | "success" | "live" {
  if (status === "ready") return "success";
  if (status === "error") return "live";
  if (status === "loading") return "warning";
  return "neutral";
}

export function statusLabel(status: PlayerCompareState["status"]): string {
  if (status === "ready") return "Tayyor";
  if (status === "error") return "Xato";
  if (status === "loading") return "Hisoblanmoqda…";
  return "Tanlanmagan";
}

export function formatClock(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}
